

const REC_LIMIT = 10;
const SKELETON_INNER_HTML =
  '<div class="cart-rec__form">' +
  '<div class="cart-rec__skeleton-media"></div>' +
  '<div class="cart-rec__body">' +
  '<div class="cart-rec__skeleton-line"></div>' +
  '<div class="cart-rec__skeleton-line cart-rec__skeleton-line--md"></div>' +
  '<div class="cart-rec__skeleton-line cart-rec__skeleton-line--sm"></div>' +
  '<div class="cart-rec__skeleton-line cart-rec__skeleton-line--btn"></div>' +
  '</div>' +
  '</div>';

class CartProductRecommendation extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.swiperEl = null;
    this.wrapperEl = null;
    this.countCurrentEl = null;
    this.countTotalEl = null;
    this.slideTemplate = null;
    this.fallbackTemplate = null;
    this.cartProductIds = new Set();
    this.anchorProductId = null;
    this.recAbortCtrl = null;
    this.pendingSync = false;
    this.isAdding = false;
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this.onChange = this.onChange.bind(this);
    this.onClick = this.onClick.bind(this);
  }

  connectedCallback() {
    this.swiperEl = this.querySelector('.cart-rec__swiper');
    this.wrapperEl = this.querySelector('.swiper-wrapper');
    this.countCurrentEl = this.querySelector('.cart-rec__count-current');
    this.countTotalEl = this.querySelector('.cart-rec__count-total');
    this.prevBtn = this.querySelector('.cart-rec__arrow--prev');
    this.nextBtn = this.querySelector('.cart-rec__arrow--next');
    this.slideTemplate = this.querySelector('.cart-rec__slide-template');
    this.fallbackTemplate = this.querySelector('.cart-rec__fallback-template');

    if (!this.swiperEl || !this.wrapperEl || !this.slideTemplate) return;

    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClick);
    document.addEventListener('cart:updated', this.onCartUpdated);

    this.populateAndBoot();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('cart:updated', this.onCartUpdated);
    if (this.recAbortCtrl) this.recAbortCtrl.abort();
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  async populateAndBoot() {
    const cart = await this.fetchCart();
    if (!cart) return;

    this.cartProductIds = new Set(cart.items.map((i) => String(i.product_id)));
    this.anchorProductId = cart.items.length ? String(cart.items[0].product_id) : null;

    // Nothing to show and nothing to fall back on — stay hidden, skip the skeleton flash.
    if (!this.anchorProductId && !this.hasFallbackSlides()) {
      this.hidden = true;
      return;
    }

    // this.renderSkeleton();
    this.setLoading(true);
    this.hidden = false;
    await this.bootSwiper();

    const products = this.anchorProductId ? await this.fetchAnchorRecommendations() : [];
    this.renderSlides(products);
    this.setLoading(false);

    if (this.realSlideCount() === 0) {
      if (this.swiper) {
        this.swiper.destroy(true, true);
        this.swiper = null;
      }
      this.hidden = true;
      return;
    }

    await this.bootSwiper();
    nudgeConversionBear();
  }

  renderSkeleton(count = 3) {
    this.wrapperEl.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const slide = document.createElement('div');
      slide.className = 'swiper-slide cart-rec__slide cart-rec__slide--skeleton';
      slide.setAttribute('aria-hidden', 'true');
      slide.innerHTML = SKELETON_INNER_HTML;
      this.wrapperEl.appendChild(slide);
    }
  }

  morphSlideToSkeleton(slide) {
    if (!slide) return;
    slide.classList.add('cart-rec__slide--skeleton');
    slide.setAttribute('aria-hidden', 'true');
    // Drop the product id so onCartUpdated's filter doesn't race us into a double-remove.
    delete slide.dataset.productId;
    slide.innerHTML = SKELETON_INNER_HTML;
  }

  setLoading(state) {
    this.classList.toggle('cart-rec--loading', !!state);
  }

  realSlideCount() {
    return Array.from(this.wrapperEl.children).filter(
      (el) => !el.classList.contains('cart-rec__slide--skeleton')
    ).length;
  }

  hasFallbackSlides() {
    return !!(
      this.fallbackTemplate &&
      this.fallbackTemplate.content.querySelector('.swiper-slide')
    );
  }

  async fetchCart() {
    try {
      const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error('[cart-rec] cart fetch', err);
      return null;
    }
  }

  async fetchAnchorRecommendations() {
    if (!this.anchorProductId) return [];
    if (this.recAbortCtrl) this.recAbortCtrl.abort();
    this.recAbortCtrl = new AbortController();
    try {
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(this.anchorProductId)}&intent=related&limit=${REC_LIMIT}`;
      const res = await fetch(url, {
        headers: { Accept: 'application/json' },
        signal: this.recAbortCtrl.signal
      });
      if (!res.ok) return [];
      const data = await res.json();
      const products = (data.products || []).filter(
        (p) => !this.cartProductIds.has(String(p.id))
      );
      return products;
    } catch (err) {
      if (err.name === 'AbortError') return [];
      console.error('[cart-rec] rec fetch', err);
      return [];
    }
  }

  renderSlides(products) {
    this.wrapperEl.innerHTML = '';
    if (products.length) {
      products.forEach((p) => {
        const slide = this.buildSlide(p);
        if (slide) this.wrapperEl.appendChild(slide);
      });
      return;
    }
    // Fallback: pre-rendered collection slides
    if (!this.fallbackTemplate) return;
    const frag = this.fallbackTemplate.content.cloneNode(true);
    frag.querySelectorAll('.swiper-slide').forEach((slide) => {
      if (this.cartProductIds.has(String(slide.dataset.productId))) slide.remove();
    });
    this.wrapperEl.appendChild(frag);
  }

  buildSlide(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const firstAvail = variants.find((v) => v.available) || variants[0];
    if (!firstAvail) return null;

    const frag = this.slideTemplate.content.cloneNode(true);
    const slide = frag.querySelector('.swiper-slide');
    if (!slide) return null;

    slide.dataset.productId = product.id;

    const form = slide.querySelector('.cart-rec__form');
    form.dataset.variants = JSON.stringify(variants);

    const url = product.url || (product.handle ? `/products/${product.handle}` : '#');
    const mediaLink = slide.querySelector('.cart-rec__media');
    mediaLink.href = url;
    mediaLink.setAttribute('aria-label', product.title || '');

    const img = slide.querySelector('.cart-rec__image');
    const imgSrc = product.featured_image || (product.images && product.images[0]);
    if (imgSrc) img.src = sizedImage(imgSrc, 200);
    img.alt = product.title || '';

    const titleLink = slide.querySelector('.cart-rec__product-link');
    titleLink.href = url;
    titleLink.textContent = product.title || '';

    const variantTitle = slide.querySelector('.cart-rec__variant-title');
    if (variantTitle) variantTitle.textContent = firstAvail.title || '';

    const price = slide.querySelector('.cart-rec__price');
    // innerHTML: the money format carries markup (currency SVG + .money spans)
    price.innerHTML = formatPrice(firstAvail.price);

    const selectsContainer = slide.querySelector('.cart-rec__selects');
    (product.options || []).forEach((option, idx) => {
      const optKey = `option${idx + 1}`;
      const optName = typeof option === 'string' ? option : (option && option.name) || '';
      if (optName.toLowerCase() === 'size') {
        const seen = new Set();
        const rawValues =
          option && Array.isArray(option.values) && option.values.length
            ? option.values
            : variants.map((v) => v[optKey]);
        const values = rawValues.filter(
          (val) => val != null && !seen.has(val) && seen.add(val)
        );

        const label = document.createElement('label');
        label.className = 'visually-hidden';
        label.setAttribute('for', `cart-rec-${product.id}-${idx}`);
        label.textContent = optName;

        const select = document.createElement('select');
        select.id = `cart-rec-${product.id}-${idx}`;
        select.className = 'cart-rec__select';
        select.dataset.optionIndex = String(idx);
        select.dataset.optionName = optName.toUpperCase();

        values.forEach((value) => {
          const opt = document.createElement('option');
          opt.value = value;
          opt.textContent = `${optName.toUpperCase()}: ${value}`;
          if (value === firstAvail[optKey]) opt.selected = true;
          select.appendChild(opt);
        });

        selectsContainer.appendChild(label);
        selectsContainer.appendChild(select);
      } else {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.className = 'cart-rec__select';
        input.dataset.optionIndex = String(idx);
        input.value = firstAvail[optKey] != null ? firstAvail[optKey] : '';
        selectsContainer.appendChild(input);
      }
    });

    slide.querySelector('.cart-rec__variant-id').value = firstAvail.id;

    const addBtn = slide.querySelector('.cart-rec__add');
    const addLabel = addBtn.querySelector('.cart-rec__add-label');
    if (firstAvail.available) {
      addBtn.disabled = false;
      addLabel.textContent = 'Add to Cart';
    } else {
      addBtn.disabled = true;
      addLabel.textContent = 'SOLD OUT';
    }

    return slide;
  }

  bootSwiper() {
    return new Promise((resolve) => {
      const create = () => {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        this.swiper = new window.Swiper(this.swiperEl, {
          slidesPerView: 1.13,
          spaceBetween: 8,
          slidesOffsetBefore: 16,
          slidesOffsetAfter: 16,
          watchOverflow: true,
          navigation: {
            prevEl: this.prevBtn,
            nextEl: this.nextBtn,
            disabledClass: 'swiper-button-disabled'
          },
          on: {
            init: () => this.updateCount(),
            slideChange: () => this.updateCount()
          }
        });
        resolve();
      };
      const tryInit = () => {
        if (typeof window.Swiper === 'undefined') {
          window.requestAnimationFrame(tryInit);
          return;
        }
        create();
      };
      tryInit();
    });
  }

  onChange(event) {
    const select = event.target.closest('.cart-rec__select');
    if (!select || select.type === 'hidden') return;
    const form = select.closest('.cart-rec__form');
    if (form) this.refreshVariant(form);
  }

  onClick(event) {
    const qtyBtn = event.target.closest('.cart-rec__qty-btn');
    if (qtyBtn) {
      event.preventDefault();
      this.changeQty(qtyBtn);
      return;
    }
    const addBtn = event.target.closest('.cart-rec__add');
    if (addBtn) {
      event.preventDefault();
      this.addToCart(addBtn);
    }
  }

  changeQty(btn) {
    const form = btn.closest('.cart-rec__form');
    if (!form) return;
    const valueEl = form.querySelector('.cart-rec__qty-value');
    const input = form.querySelector('.cart-rec__qty-input');
    const action = btn.dataset.qtyAction;
    let qty = parseInt(valueEl.textContent, 10) || 1;
    qty = action === 'inc' ? qty + 1 : Math.max(1, qty - 1);
    valueEl.textContent = qty;
    input.value = qty;
    const decBtn = form.querySelector('[data-qty-action="dec"]');
    if (decBtn) decBtn.disabled = qty <= 1;
  }

  refreshVariant(form) {
    const selects = form.querySelectorAll('.cart-rec__select');
    const variantInput = form.querySelector('.cart-rec__variant-id');
    const addBtn = form.querySelector('.cart-rec__add');
    const label = addBtn.querySelector('.cart-rec__add-label');
    const titleEl = form.querySelector('.cart-rec__variant-title');
    let variants;
    try {
      variants = JSON.parse(form.dataset.variants);
    } catch (e) {
      return;
    }
    const selected = Array.from(selects).map((s) => s.value);
    const match = variants.find((v) =>
      (v.options || []).every((opt, i) => opt === selected[i])
    );

    if (titleEl && match) titleEl.textContent = match.title;

    if (match && match.available) {
      variantInput.value = match.id;
      addBtn.disabled = false;
      label.textContent = 'Add to Cart';
    } else if (match) {
      variantInput.value = match.id;
      addBtn.disabled = true;
      label.textContent = 'SOLD OUT';
    } else {
      variantInput.value = '';
      addBtn.disabled = true;
      label.textContent = 'UNAVAILABLE';
    }
  }

  async addToCart(btn) {
    if (btn.disabled || btn.dataset.loading === 'true') return;
    const form = btn.closest('.cart-rec__form');
    const slide = btn.closest('.swiper-slide');
    if (!form || !slide) return;
    const variantId = form.querySelector('.cart-rec__variant-id').value;
    if (!variantId) return;

    btn.dataset.loading = 'true';
    btn.disabled = true;
    this.isAdding = true;

    try {
      const formData = new FormData(form);
      const request = new Request('/cart/add.js', {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' }
      });
      const res = await fetch(request);
      if (!res.ok) throw new Error('Add to cart failed: ' + res.status);

      const productId = slide.dataset.productId;
      if (productId) this.cartProductIds.add(String(productId));

      // Show shimmer skeleton on the just-added slide, then remove it.
      this.morphSlideToSkeleton(slide);
      this.updateCount();

      document.documentElement.dispatchEvent(
        new CustomEvent('theme:cartchanged', { bubbles: true, cancelable: false })
      );
      document.dispatchEvent(new CustomEvent('cart:updated'));

      await new Promise((resolve) => setTimeout(resolve, 500));
      this.removeSlide(slide);
    } catch (err) {
      console.error('[cart-rec]', err);
      btn.dataset.loading = 'false';
      btn.disabled = false;
    } finally {
      this.isAdding = false;
    }
  }

  removeSlide(slide) {
    if (!this.swiper || !slide || !slide.parentNode) return;
    const index = Array.prototype.indexOf.call(this.wrapperEl.children, slide);
    if (index < 0) return;
    this.swiper.removeSlide(index);
    this.updateCount();
    this.checkEmpty();
  }

  async onCartUpdated() {
    if (this.pendingSync) return;
    this.pendingSync = true;
    try {
      const cart = await this.fetchCart();
      if (!cart) return;
      this.cartProductIds = new Set(cart.items.map((i) => String(i.product_id)));
      const newAnchor = cart.items.length ? String(cart.items[0].product_id) : null;

      // When the user adds from our own swiper, addToCart already drives the
      // per-slide skeleton + removal locally. Skip any full rebuild here so the
      // skeleton doesn't appear a second time across the whole list.
      if (this.isAdding) {
        this.anchorProductId = newAnchor;
        return;
      }

      if (newAnchor !== this.anchorProductId) {
        this.anchorProductId = newAnchor;

        if (!newAnchor && !this.hasFallbackSlides()) {
          if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
          }
          this.wrapperEl.innerHTML = '';
          this.hidden = true;
          return;
        }

        // Anchor changed → show skeleton while we fetch the new recs.
        this.renderSkeleton();
        this.setLoading(true);
        this.hidden = false;
        await this.bootSwiper();

        const products = newAnchor ? await this.fetchAnchorRecommendations() : [];
        this.renderSlides(products);
        this.setLoading(false);

        if (this.realSlideCount() === 0) {
          if (this.swiper) {
            this.swiper.destroy(true, true);
            this.swiper = null;
          }
          this.hidden = true;
          return;
        }
        await this.bootSwiper();
        nudgeConversionBear();
      } else {
        // Anchor unchanged — only filter out items now in cart
        const removeIdx = [];
        Array.from(this.wrapperEl.children).forEach((slide, i) => {
          if (this.cartProductIds.has(String(slide.dataset.productId))) removeIdx.push(i);
        });
        if (removeIdx.length && this.swiper) this.swiper.removeSlide(removeIdx);
        this.updateCount();
        this.checkEmpty();
      }
    } catch (err) {
      console.error('[cart-rec] sync', err);
    } finally {
      this.pendingSync = false;
    }
  }

  updateCount() {
    if (!this.countCurrentEl || !this.countTotalEl) return;
    const total = this.realSlideCount();
    const current = total === 0 ? 0 : this.swiper ? this.swiper.realIndex + 1 : 1;
    this.countCurrentEl.textContent = current;
    this.countTotalEl.textContent = total;
  }

  checkEmpty() {
    this.hidden = this.realSlideCount() === 0;
  }
}

function sizedImage(url, width) {
  if (!url) return url;
  const sep = url.indexOf('?') === -1 ? '?' : '&';
  return `${url}${sep}width=${width}`;
}

function formatPrice(price) {
  // /recommendations/products.json returns price as integer cents in the
  // shop's base currency. Delegate to theme.formatMoneyConverted, which
  // returns HTML in the shop's money format. Conversion to the shopper's
  // selected currency is then driven by Conversion Bear via
  // nudgeConversionBear() after the slides are appended.
  if (window.theme && typeof window.theme.formatMoneyConverted === 'function') {
    return window.theme.formatMoneyConverted(price);
  }
  var cents = typeof price === 'number' ? price : parseInt(price, 10);
  if (!isFinite(cents)) return '';
  return 'Dhs. ' + (cents / 100).toFixed(2);
}

function nudgeConversionBear() {
  if (window.theme && typeof window.theme.nudgeConversionBear === 'function') {
    window.theme.nudgeConversionBear();
    return;
  }
  var attempts = 0;
  var run = function () {
    var cb = window.conversionBearAutoCurrencyConverter;
    if (cb && typeof cb.convertPricesOnPage === 'function') {
      try { cb.convertPricesOnPage(); } catch (e) { }
      return;
    }
    if (attempts++ < 20) setTimeout(run, 200);
  };
  run();
}

customElements.define('cart-product-recommendation', CartProductRecommendation);

